/** Contenu paramétrable d'une confirmation (voir `ConfirmDialog.confirm`). */
export interface ConfirmDialogOptions {
  title?: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
}

/**
 * Confirmation générique à 2 boutons (voir `Shape.urlAction.confirmBeforeOpen`,
 * `createTokaMap.ts`'s `shape:click`) — même patron visuel que `ui/MigrationDialog`, mais
 * paramétrable (titre/message/libellés) et, contrairement à elle, annulable par clic extérieur ou
 * Échap (ici la décision n'est jamais bloquante : annuler revient juste à ne pas ouvrir le lien).
 */
export class ConfirmDialog {
  private element: HTMLDivElement | null = null;

  constructor(private container: HTMLElement) {}

  /** Résout `true` si l'utilisateur confirme, `false` s'il annule (bouton, clic extérieur ou Échap). */
  confirm(options: ConfirmDialogOptions): Promise<boolean> {
    this.close();

    return new Promise<boolean>((resolve) => {
      const overlay = document.createElement("div");
      overlay.className = "toka-confirm-dialog-overlay";

      const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Escape") settle(false);
      };

      const settle = (accepted: boolean) => {
        document.removeEventListener("keydown", handleKeyDown);
        this.close();
        resolve(accepted);
      };

      overlay.addEventListener("click", (event) => {
        if (event.target === overlay) settle(false);
      });
      document.addEventListener("keydown", handleKeyDown);

      const dialog = document.createElement("div");
      dialog.className = "toka-confirm-dialog";

      if (options.title) {
        const title = document.createElement("div");
        title.className = "toka-confirm-dialog__title";
        title.textContent = options.title;
        dialog.appendChild(title);
      }

      const text = document.createElement("div");
      text.className = "toka-confirm-dialog__text";
      text.textContent = options.message;
      dialog.appendChild(text);

      const actions = document.createElement("div");
      actions.className = "toka-confirm-dialog__actions";

      const cancelButton = document.createElement("button");
      cancelButton.type = "button";
      cancelButton.className = "toka-confirm-dialog__cancel-button";
      cancelButton.textContent = options.cancelText ?? "Annuler";
      cancelButton.addEventListener("click", () => settle(false));
      actions.appendChild(cancelButton);

      const confirmButton = document.createElement("button");
      confirmButton.type = "button";
      confirmButton.className = "toka-confirm-dialog__confirm-button";
      confirmButton.textContent = options.confirmText ?? "Continuer";
      confirmButton.addEventListener("click", () => settle(true));
      actions.appendChild(confirmButton);

      dialog.appendChild(actions);
      overlay.appendChild(dialog);
      this.container.appendChild(overlay);
      this.element = overlay;
    });
  }

  private close(): void {
    this.element?.remove();
    this.element = null;
  }
}
